import { useCallback, useRef } from 'react';
import { useLazySearchEmployeesQuery } from '@dap/common/data-access';
import { EmployeeSearchResult, EmployeeStatus } from '@dap/memberadmin/types';
import {
  findReusablePrefix,
  MAX_REUSABLE_RESULTS,
  narrowEmployeeResults,
} from './mentionSearchResults';

type SearchCache = {
  scopeKey: string;
  // Keyed by lowercased query. Holds the promise so an in-flight search can be reused too.
  entries: Map<string, Promise<EmployeeSearchResult[]>>;
};

/**
 * Brand-scoped employee search for @-mentions that remembers its results. Once a query
 * has been searched, longer queries starting with it are answered by narrowing those
 * results locally instead of calling the search API again.
 */
export function useEmployeeSearchCache() {
  const [triggerSearch, searchState] = useLazySearchEmployeesQuery();
  const cache = useRef<SearchCache>({ scopeKey: '', entries: new Map() });

  // Cached searches for the given brands. A brand change starts over.
  const entriesFor = useCallback((brandKeys: string[]) => {
    const scopeKey = brandKeys.join(',');
    if (cache.current.scopeKey !== scopeKey) {
      cache.current = { scopeKey, entries: new Map() };
    }
    return cache.current.entries;
  }, []);

  /** Searches the API for enabled employees and remembers the results */
  const fetchEmployees = useCallback(
    (query: string, brandKeys: string[]) => {
      const entries = entriesFor(brandKeys);
      const key = query.toLowerCase();
      const promise = triggerSearch({
        filters: { query, brandFilter: brandKeys, enabledStatusFilter: true },
        operator: 'AND',
        brandOnly: true,
      })
        .unwrap()
        .then((results) =>
          results.filter((employee) => employee.status === EmployeeStatus.ENABLED)
        );
      entries.set(key, promise);
      // Let a later search retry instead of reusing the failure
      promise.catch(() => {
        if (entries.get(key) === promise) {
          entries.delete(key);
        }
      });
      return promise;
    },
    [triggerSearch, entriesFor]
  );

  /**
   * Results for `query` from an earlier (possibly still running) search, or undefined if
   * there is none to reuse. Resolves to null when the API has to be asked after all.
   */
  const fromCache = useCallback(
    (query: string, brandKeys: string[]): Promise<EmployeeSearchResult[] | null> | undefined => {
      const needle = query.toLowerCase();
      const entries = entriesFor(brandKeys);
      const prefix = findReusablePrefix(entries.keys(), needle);
      const cached = prefix === undefined ? undefined : entries.get(prefix);
      if (!cached) {
        return undefined;
      }
      return cached.then(
        (results) => {
          if (prefix === needle) {
            return results;
          }
          // A large result set may be truncated, so it can't be narrowed safely
          return results.length < MAX_REUSABLE_RESULTS
            ? narrowEmployeeResults(results, needle)
            : null;
        },
        () => null
      );
    },
    [entriesFor]
  );

  return { fetchEmployees, fromCache, isFetching: searchState.isFetching };
}
